import { RoleName } from "@/api/users-types";
import { ApiErrorAlert } from "@/components/ui/ApiErrorAlert";
import { useHasRole } from "@/hooks/use-has-role";
import { useCreatePromptRevision } from "@/queries/prompts/use-create-prompt-revision";
import { usePromptById } from "@/queries/prompts/use-prompt-by-id";
import { getLatest } from "@/utils/get-latest";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router";

export function CreateRevision({
  disabled,
  promptId,
}: {
  disabled: boolean;
  promptId: string;
}) {
  const [open, setOpen] = useState(false);

  const canCreate = useHasRole(RoleName.promptEngineer);

  if (!canCreate) {
    return null;
  }

  return (
    <>
      <CreateRevisionButton disabled={disabled} onClick={() => setOpen(true)} />
      <CreateRevisionDialog
        open={open}
        promptId={promptId}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

function CreateRevisionButton({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip
      title={
        !disabled ? "" : "You can only edit the latest revision of a prompt"
      }
      placement="bottom"
      disableInteractive
    >
      <Box>
        <Button variant="contained" disabled={disabled} onClick={onClick}>
          New revision
        </Button>
      </Box>
    </Tooltip>
  );
}

function CreateRevisionDialog({
  open,
  promptId,
  onClose,
}: {
  open: boolean;
  promptId: string;

  onClose: () => void;
}) {
  const { data: prompt } = usePromptById(promptId);

  const navigate = useNavigate();

  const {
    mutate: createRevision,
    isPending,
    error,
  } = useCreatePromptRevision();

  // undefined means input not touched
  const [newPrompt, setNewPrompt] = useState<string>();
  const [revisionNote, setRevisionNote] = useState<string>();

  const [newPromptError, setNewPromptError] = useState<string>();

  if (!prompt) {
    return null;
  }

  const latestRevision = getLatest(prompt.revisions);

  const initialPromptText = latestRevision?.prompt ?? "";
  const initialRevisionNote = "";

  function handleClose() {
    setNewPrompt(undefined);
    setRevisionNote(undefined);
    setNewPromptError(undefined);
    onClose();
  }

  function handleCreate() {
    const isPromptTextUnchanged =
      newPrompt === undefined || newPrompt === initialPromptText;
    if (isPromptTextUnchanged) {
      setNewPromptError(
        "New prompt must be different from the current revision",
      );
      return;
    }
    if (!newPrompt) {
      setNewPromptError("Prompt required");
      return;
    }
    createRevision(
      { promptId, data: { prompt: newPrompt, revisionNote } },
      {
        onSuccess: () => {
          handleClose();
          navigate(`/prompts/${promptId}`); // makes sure the user sees the newly created revision
        },
      },
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <form>
        <DialogTitle>Create New Prompt Revision</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={error ? 0 : 0.75}>
            {error && <ApiErrorAlert error={error} />}
            <CreateRevisionForm
              prompt={newPrompt ?? initialPromptText}
              promptError={newPromptError}
              revisionNote={revisionNote ?? initialRevisionNote}
              onPromptChange={(value) => {
                setNewPrompt(value);
                setNewPromptError(undefined);
              }}
              onRevisionNoteChange={(value) => setRevisionNote(value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            loading={isPending}
            variant={"contained"}
          >
            Create
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export function CreateRevisionForm({
  prompt,
  promptError,
  revisionNote,
  onPromptChange,
  onRevisionNoteChange,
}: {
  prompt: string;
  promptError?: string;
  revisionNote: string;
  onPromptChange: (value: string) => void;
  onRevisionNoteChange: (value: string) => void;
}) {
  return (
    <>
      <TextField
        label="Revision note"
        id="create-revision-form_revision-note"
        value={revisionNote}
        onChange={(e) => {
          onRevisionNoteChange(e.target.value);
        }}
      />
      <TextField
        label="Prompt*"
        id="create-revision-form_prompt"
        error={!!promptError}
        helperText={promptError}
        multiline={true}
        minRows={15}
        type="text"
        value={prompt}
        onChange={(e) => {
          onPromptChange(e.target.value);
        }}
      />
    </>
  );
}
